import { Button, ButtonGroup, DeleteIcon, EditIcon, ImportIcon, Table, Tooltip } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useIsUsedInLesson } from '../../../hooks/useIsUsedInLesson';
import { UsedInLessonsTooltipContent } from '../../TooltipContents/LessonUsedInTooltipContent/UsedInLessonsTooltipContent';
import { useClassesTable } from '../hooks/useClassesTable';
import { ClassesTableType } from '../types/ClassesTypes';

type SettingsClassesTableProps = {
  loading: boolean;
  onAdd: () => void;
  onInherit: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
};

export const SettingsClassesTable = observer(
  ({ loading, onAdd, onInherit, onEdit, onDelete }: SettingsClassesTableProps) => {
    const { t } = useTranslation();

    const check = useIsUsedInLesson();

    const {
      data,
      columns,
      tableSettings,
      tableSorting,
      tableVisibility,
      tableSelection,
      emptyStateSettings,
      printerSettings,
      actionBarSettings,
    } = useClassesTable({
      context: 'settings',
      columns: [
        'name',
        'shortName',
        'grade',
        'gradeGroup',
        'tutor1',
        'tutor2',
        'tutor3',
        'room',
        'printOrder',
        'printPageNr',
        'color',
      ],
    });

    return (
      <Table<ClassesTableType>
        loading={loading}
        data={data}
        columns={columns}
        {...tableSettings}
        {...tableSorting}
        {...tableVisibility}
        {...tableSelection}
        printerSettings={printerSettings}
        actionBarSettings={{
          ...actionBarSettings,
          extendedActionsRight: (
            <Button hierarchy='tertiary' icon={<ImportIcon />} onClick={onInherit}>
              {t('common.inherit')}
            </Button>
          ),
        }}
        emptyStateSettings={{
          ...emptyStateSettings,
          onButtonClick: onInherit,
        }}
        onAddClick={onAdd}
        lastCol={(row) => {
          const used = check(row.original.uuid, 'class');
          const deleteButton = (
            <Button
              disabled={used.isUsed}
              hierarchy='secondary'
              type='button'
              icon={<DeleteIcon className='small' color='var(--color-error' />}
              onClick={() => onDelete(row.original.uuid)}
            />
          );
          return (
            <ButtonGroup>
              <Button
                hierarchy='secondary'
                type='button'
                onClick={() => onEdit(row.original.uuid)}
                icon={<EditIcon className='small' />}
              />
              {used.isUsed ? (
                <Tooltip
                  content={
                    <UsedInLessonsTooltipContent lessons={used.lessons} usedInText={t('classes.usedInLesson')} />
                  }
                >
                  {deleteButton}
                </Tooltip>
              ) : (
                deleteButton
              )}
            </ButtonGroup>
          );
        }}
      />
    );
  },
);
